"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Icon, IconName } from "../icons/Icon";
import { spacing } from "~/utils/tailwindUtils";
import Card from "../cards/Card";
import Flex from "../flex/Flex";
import { twMerge } from "tailwind-merge";
import Text from "../texts/Text";

type RightSideModalProps = {
  header: string | React.ReactNode;
  isOpen: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  outsideClick?: boolean;
  headerClassName?: string;
  cardClassName?: string;
  modalClassName?: string;
  childrenDivWrapperClassName?: string;
  animationDuration?: number;
};

/**
 * Provides a modal component that can be used to display content towards right of the screen with a close button.
 * @param header - header text of the modal
 * @param isOpen - boolean to determine if the modal is open
 * @param closeModal - function to close the modal
 * @param children - content to display in the modal
 * @param outsideClick - boolean to determine if the modal should close when clicking outside of it
 * @param className - custom class name for the modal
 * @param modalClassName - custom class name for the modal container
 * @param childrenDivWrapperClassName - custom class name for the children wrapper
 * @param width - width of the modal, default is 'sm'
 * @param animationDuration - duration of the animation in milliseconds, default is 1000ms
 * @param isCloseButtonHidden - boolean to determine if the close button should be hidden
 * @param size - size of the modal
 * @returns
 */
const RightSideModal = ({
  header,
  isOpen,
  onRequestClose,
  children,
  outsideClick,
  headerClassName,
  cardClassName,
  modalClassName,
  childrenDivWrapperClassName,
  animationDuration = 750, // milliseconds
}: RightSideModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const closeWithAnimation = useCallback(() => {
    setIsVisible(false);
    onRequestClose();
  }, [onRequestClose]);

  // Handle mount/unmount with animation
  useEffect(() => {
    let mountTimer: number | undefined;
    let showTimer: number | undefined;
    let hideTimer: number | undefined;
    let unmountTimer: number | undefined;

    if (isOpen) {
      mountTimer = window.setTimeout(() => {
        setIsMounted(true);
        showTimer = window.setTimeout(() => {
          setIsVisible(true);
        }, 20);
      }, 0);
    } else {
      hideTimer = window.setTimeout(() => {
        setIsVisible(false);
      }, 0);
      unmountTimer = window.setTimeout(() => {
        setIsMounted(false);
      }, animationDuration);
    }

    return () => {
      if (mountTimer) window.clearTimeout(mountTimer);
      if (showTimer) window.clearTimeout(showTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
      if (unmountTimer) window.clearTimeout(unmountTimer);
    };
  }, [isOpen, animationDuration]);

  // Close the modal if clicking outside
  useEffect(() => {
    if (!outsideClick) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeWithAnimation();
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, closeWithAnimation, outsideClick]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeWithAnimation();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeWithAnimation]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  if (!isMounted && !isVisible) return null;

  return (
    <div className="fixed inset-0 z-90 overflow-hidden">
      {/* Overlay */}
      <div
        className={twMerge(
          "fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity",
          isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        style={{ transitionDuration: `${animationDuration}ms` }}
        onClick={outsideClick ? closeWithAnimation : undefined}
      />

      {/* Modal container */}
      <div
        ref={modalRef}
        className={twMerge("fixed inset-y-0 right-0 w-[475px] lg:w-[575px] bg-white shadow-xl", modalClassName)}
        style={{
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          willChange: "transform",
        }}
      >
        {/* Modal content */}
        <Flex className="flex-col h-full">
          {/* Sticky Header */}
          <Flex
            className={twMerge(
              "sticky top-0 z-10 bg-white px-10 pt-10 pb-4 justify-between gap-4 items-center shrink-0",
              headerClassName
            )}
          >
            {typeof header === "string" ? <Text className="text-sm md:text-lg font-bold">{header}</Text> : header}
            <Flex className="p-[8px] rounded-full hover:bg-gray-50 cursor-pointer" onClick={closeWithAnimation}>
              <Icon name={IconName.Cancel} size={spacing[4]} customStrokeWidth={2} colorMode="fill" />
            </Flex>
          </Flex>
          {/* Scrollable Content */}
          <Card className={twMerge("flex-1 overflow-y-auto hide-scrollbar px-10 pb-10 border-0 gap-6", cardClassName)}>
            <Flex className={twMerge("flex-col gap-4 w-full h-full", childrenDivWrapperClassName)}>{children}</Flex>
          </Card>
        </Flex>
      </div>
    </div>
  );
};

export default RightSideModal;
